'use client';

import { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../../libs/utils';
import { IconButton } from '../../button';
import { BaseTopNavigation } from '../../topNavigation';
import { BaseOverlay } from '../overlay/Overlay';

const TRANSITION_MS = 380;

export type FullScreenModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  navigationTitle?: ReactNode;
  titleAction?: () => void;
  fullWidth?: boolean;
  rightActions?: ReactNode;
  children: ReactNode;
  className?: string;
};

export const FullScreenModal = ({
  open,
  onOpenChange,
  navigationTitle,
  titleAction,
  fullWidth = false,
  rightActions,
  children,
  className
}: FullScreenModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  useEffect(() => {
    if (!mounted) return;

    if (open) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEntered(true);
        });
      });
      return () => cancelAnimationFrame(id);
    }

    setEntered(false);
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted || typeof document === 'undefined') return;

    const { body } = document;
    const prevOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prevOverflow;
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mounted, handleClose]);

  useEffect(() => {
    if (open || !mounted) return;

    const t = window.setTimeout(() => {
      setMounted(false);
    }, TRANSITION_MS);

    return () => window.clearTimeout(t);
  }, [open, mounted]);

  const handlePanelTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== 'transform') return;
    if (!open) {
      setMounted(false);
    }
  };

  const headerRight = (
    <div className="flex shrink-0 items-center gap-tiny-small">
      {rightActions}
      <IconButton type="button" iconType="close" size="small" variant="main" onClick={handleClose} aria-label="닫기" />
    </div>
  );

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  const portalTarget = document.body;

  const dialogAriaLabel = typeof navigationTitle === 'string' || typeof navigationTitle === 'number' ? String(navigationTitle) : '내용 보기';

  return createPortal(
    <div className="fixed inset-0 z-dialog flex min-h-0 w-full flex-col">
      <BaseOverlay
        aria-hidden
        className={cn('absolute inset-0 !z-0 transition-opacity duration-300 ease-out motion-reduce:duration-0', entered ? 'opacity-100' : 'opacity-0')}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={dialogAriaLabel}
        onTransitionEnd={handlePanelTransitionEnd}
        className={cn(
          'absolute inset-0 z-10 flex h-full min-h-0 w-full flex-col overflow-hidden bg-white transition-transform duration-300 ease-out will-change-transform motion-reduce:duration-0',
          entered ? 'translate-y-0' : 'translate-y-full',
          className
        )}
      >
        <BaseTopNavigation bg fullWidth={fullWidth} title={navigationTitle} titleAction={titleAction} rightAction={headerRight} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
      </div>
    </div>,
    portalTarget
  );
};
